import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import {
  useGoalIntroForm,
  useRegistrationRouteState,
} from '../features/registration/model';
import {
  GoalIntroBackButton,
  GoalIntroContinueButton,
  GoalIntroField,
  GoalIntroHeading,
  GoalIntroHero,
  GoalIntroSkipButton,
} from '../features/registration/ui';
import { getEmployeeGoal } from '../shared/api/employee.service';
import { useEmployeeSession } from '../shared/hooks/use-employee-session';

const FOOTER_GAP_PX = 14;
const FOOTER_BOTTOM_PADDING_PX = 8;
const FOOTER_SECONDARY_MIN_HEIGHT_PX = 36;
const FOOTER_SECONDARY_ERROR_MIN_HEIGHT_PX = 52;

const Screen = styled.div.attrs({
  'data-ui': 'registration-goals-goal-page',
})`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;

  & > [data-ui='registration-goals-goal-intro-header'] {
    margin-bottom: ${({ theme }) => theme.scale.px(15)};
  }

  & > [data-ui='registration-goals-goal-intro-hero'] {
    align-self: center;
    margin-bottom: ${({ theme }) => theme.scale.px(15)};
  }

  & > [data-ui='registration-goals-goal-intro-field'] {
    align-self: center;
    margin-bottom: auto;
  }
`;

const IntroHeader = styled.div.attrs({
  'data-ui': 'registration-goals-goal-intro-header',
})`
  display: grid;
  gap: ${({ theme }) => theme.scale.px(25)};

  & > [data-ui='registration-goals-goal-intro-back-button'] {
    margin-left: ${({ theme }) => theme.scale.px(35)};
  }
`;

const SubmitError = styled.p`
  margin: 0;
  text-align: center;
  font-size: ${({ theme }) => theme.scale.px(12)};
  font-weight: 500;
  line-height: 1.2;
  color: ${({ theme }) => theme.colors.support.danger};
`;

const Footer = styled.div.attrs({
  'data-ui': 'registration-goals-goal-page-footer',
})`
  flex-shrink: 0;
  display: grid;
  justify-items: center;
  row-gap: ${({ theme }) => theme.scale.px(FOOTER_GAP_PX)};
  padding-bottom: ${({ theme }) => theme.scale.px(FOOTER_BOTTOM_PADDING_PX)};
`;

const FooterSecondary = styled.div.attrs({
  'data-ui': 'registration-goals-goal-page-footer-secondary',
})<{ $minHeightPx: number }>`
  width: 100%;
  min-height: ${({ $minHeightPx, theme }) => theme.scale.px($minHeightPx)};
  display: grid;
  justify-items: center;
  align-content: start;
`;

function resolveGoalFormKey(goalText?: string, targetAmount?: number): string {
  return JSON.stringify([goalText ?? null, targetAmount ?? null]);
}

type GoalPageFormProps = {
  initialGoalText?: string;
  initialTargetAmount?: number;
};

function GoalPageForm({
  initialGoalText,
  initialTargetAmount,
}: GoalPageFormProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const goalIntroForm = useGoalIntroForm({
    initialGoalText,
    initialTargetAmount,
  });
  const registrationRoute = useRegistrationRouteState();
  const previousPath = registrationRoute.previousImplementedPath;
  const nextPath = registrationRoute.nextImplementedPath;

  return (
    <Screen>
      <IntroHeader>
        <GoalIntroBackButton
          ariaLabel={t('registrationGoals.backAria')}
          onPress={() => previousPath && navigate(previousPath)}
        />
        <GoalIntroHeading />
      </IntroHeader>
      <GoalIntroHero />
      <GoalIntroField
        goalText={goalIntroForm.goalText}
        onGoalTextChange={goalIntroForm.setGoalText}
      />
      <Footer>
        <GoalIntroContinueButton
          canSubmit={goalIntroForm.canSubmit && !goalIntroForm.isSubmitting}
          onContinue={async () => {
            if (
              !goalIntroForm.canSubmit ||
              goalIntroForm.isSubmitting ||
              !nextPath
            ) {
              return;
            }

            const isSubmitted = await goalIntroForm.submit();
            if (!isSubmitted) {
              return;
            }

            navigate(nextPath);
          }}
        />
        <FooterSecondary
          $minHeightPx={
            goalIntroForm.submitError
              ? FOOTER_SECONDARY_ERROR_MIN_HEIGHT_PX
              : FOOTER_SECONDARY_MIN_HEIGHT_PX
          }
        >
          <GoalIntroSkipButton
            isDisabled={goalIntroForm.isSubmitting}
            onSkip={async () => {
              if (goalIntroForm.isSubmitting || !nextPath) {
                return;
              }

              const isSkipped = await goalIntroForm.skip();
              if (!isSkipped) {
                return;
              }

              navigate(nextPath);
            }}
          />
          {goalIntroForm.submitError ? (
            <SubmitError>{goalIntroForm.submitError}</SubmitError>
          ) : null}
        </FooterSecondary>
      </Footer>
    </Screen>
  );
}

export function GoalPage() {
  const { status } = useEmployeeSession();
  const goalQuery = useQuery({
    enabled: status === 'authenticated',
    queryFn: getEmployeeGoal,
    queryKey: ['employee', 'goal'],
  });
  const goal = goalQuery.data ?? null;

  return (
    <GoalPageForm
      key={resolveGoalFormKey(goal?.text, goal?.targetAmount)}
      initialGoalText={goal?.text}
      initialTargetAmount={goal?.targetAmount}
    />
  );
}
